/**
 * `/admin/tracker-sources` — admin-only CRUD for Jira/ADO instances.
 *
 * A tracker source is "where we talk to" (Jira Cloud URL + API token).
 * Credentials are write-only — the list shows a boolean marker, never
 * the token itself, and rotation is a PATCH with a fresh `token` field.
 */

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eraser, Loader2, Pencil, Plus, Trash2, WifiOff } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Select } from '@lean-agility/design-system/react';

import { Dialog, DialogActions } from '@/components/dialog';
import { useClearSourceCache, useCreateSource, useDeleteSource, useSources, useUpdateSource } from '../hooks';
import type { SourceResponse, TrackerAuthMethod, TrackerKind } from '../types';
import { TrackerCapabilityGate } from '../components/TrackerCapabilityGate';
import { AUTH_METHOD_SHAPE, hasRequiredUser, normalizeAuthUser, secretFieldLabel } from '../authMethod';

// Whether `auth_user` is required, and what it is called, both come from
// AUTH_METHOD_SHAPE — never re-spelled here. Zod refines on that rule so the
// message renders next to the field the user actually needs to fix.
const sharedFields = {
  kind: z.enum(['jira', 'ado']),
  name: z.string().min(1, 'Name is required'),
  base_url: z.string().url('Must be a valid URL'),
  // Derived from the shape table so a method added upstream can't be silently
  // rejected here by a stale literal list.
  auth_method: z.enum(Object.keys(AUTH_METHOD_SHAPE) as [TrackerAuthMethod, ...TrackerAuthMethod[]]),
  auth_user: z.string().optional(),
  // Optional PEM CA bundle for on-prem trackers behind a private/corporate
  // CA. Left empty for Cloud instances (public trust roots suffice).
  tls_ca_cert: z.string().optional(),
};

const createSchema = z
  .object({
    ...sharedFields,
    token: z.string().min(1, 'Token is required'),
  })
  .refine(hasRequiredUser, {
    message: 'Email (Cloud) or username (Basic) is required',
    path: ['auth_user'],
  });

const updateSchema = z
  .object({
    ...sharedFields,
    token: z.string().optional(),
  })
  .refine(hasRequiredUser, {
    message: 'Email (Cloud) or username (Basic) is required',
    path: ['auth_user'],
  });

type CreateFormData = z.infer<typeof createSchema>;
type UpdateFormData = z.infer<typeof updateSchema>;

export function SourcesAdminPage() {
  return (
    <TrackerCapabilityGate>
      <SourcesAdminPageContent />
    </TrackerCapabilityGate>
  );
}

function SourcesAdminPageContent() {
  const { data: sources, isLoading, error } = useSources();
  const [editing, setEditing] = useState<SourceResponse | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="container mx-auto space-y-6 px-4 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tracker sources</h1>
          <p className="mt-1 text-sm text-text-muted">
            Jira / Azure DevOps instances. Credentials never leave the server — rotate by editing and providing a new
            token.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Add source
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registered sources</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-feedback-error">Failed to load sources. Please refresh.</p>}
          {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
          {!isLoading && sources && sources.length === 0 && (
            <p className="text-sm text-text-muted">No sources yet — add your first tracker instance above.</p>
          )}
          {sources && sources.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-text-muted">
                <tr>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Kind</th>
                  <th className="py-2 pr-4">Base URL</th>
                  <th className="py-2 pr-4">Credentials</th>
                  <th className="w-24 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <SourceRow key={s.id} source={s} onEdit={() => setEditing(s)} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {creating && <SourceFormDialog source={null} onClose={() => setCreating(false)} />}
      {editing && <SourceFormDialog source={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SourceRow({ source, onEdit }: { source: SourceResponse; onEdit: () => void }) {
  const deleteSource = useDeleteSource();
  const clearCache = useClearSourceCache();
  const onDelete = async () => {
    if (!confirm(`Delete source "${source.name}"? This will not remove datasets that reference it.`)) return;
    await deleteSource.mutateAsync(source.id);
  };
  // The confirm text carries the two facts the button itself cannot: the cache
  // is keyed per tracker instance (so EVERY dataset on this host is affected,
  // not just the one being debugged), and the cost is a full refetch rather
  // than any loss of shipcast data.
  const onClearCache = async () => {
    if (
      !confirm(
        `Clear the cached work items for ${source.base_url}?\n\n` +
          'No shipcast data is lost — imported deliverables and past ingestions are untouched. ' +
          'But the next refresh of EVERY dataset pointed at this tracker instance will refetch ' +
          'every work item from scratch, which can take several minutes on a large instance.\n\n' +
          'Use this when the tracker changed something shipcast cannot detect — a renamed status, ' +
          'a workflow migration — and a refresh keeps returning stale results.',
      )
    )
      return;
    await clearCache.mutateAsync(source.id);
  };
  return (
    <tr
      className="
        border-b
        last:border-b-0
      "
    >
      <td className="py-2 pr-4 font-medium">{source.name}</td>
      <td className="py-2 pr-4 uppercase">{source.kind}</td>
      <td className="max-w-xs truncate py-2 pr-4 text-text-muted">{source.base_url}</td>
      <td className="py-2 pr-4">
        {source.has_credentials ? (
          <span
            className="
              inline-flex items-center rounded-sm bg-feedback-success/10 px-2 py-0.5 text-xs text-feedback-success
            "
          >
            configured
          </span>
        ) : (
          <span
            className="
              inline-flex items-center rounded-sm bg-feedback-warning/10 px-2 py-0.5 text-xs text-feedback-warning
            "
          >
            missing
          </span>
        )}
      </td>
      <td className="py-2 text-right">
        <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Edit source">
          <Pencil className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearCache}
          disabled={clearCache.isPending}
          aria-label="Clear cached work items"
          title="Clear cached work items — forces a full refetch on the next refresh"
        >
          {clearCache.isPending ? <Loader2 className="size-4 animate-spin" /> : <Eraser className="size-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={deleteSource.isPending}
          aria-label="Delete source"
        >
          <Trash2 className="size-4" />
        </Button>
        {clearCache.data && <span className="ml-2 text-xs text-text-muted">cleared {clearCache.data.cleared}</span>}
      </td>
    </tr>
  );
}

function SourceFormDialog({ source, onClose }: { source: SourceResponse | null; onClose: () => void }) {
  const isEditing = !!source;
  const createMut = useCreateSource();
  const updateMut = useUpdateSource();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEditing ? updateSchema : createSchema),
    defaultValues: {
      kind: 'jira',
      name: '',
      base_url: '',
      token: '',
      auth_method: 'api_token',
      auth_user: '',
      tls_ca_cert: '',
    },
  });

  useEffect(() => {
    if (source) {
      reset({
        kind: source.kind,
        name: source.name,
        base_url: source.base_url,
        token: '',
        auth_method: source.auth_method,
        auth_user: source.auth_user ?? '',
        // The stored PEM is never echoed — leave empty; paste to replace.
        tls_ca_cert: '',
      });
    }
  }, [source, reset]);

  // react-hook-form's `watch()` returns a non-memoizable function by design, so
  // the React Compiler cannot memoize it — this is a documented, unavoidable
  // library incompatibility, not a fixable code smell (ref shipcast-7i0).
  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedAuthMethod = (watch('auth_method') ?? 'api_token') as TrackerAuthMethod;
  const userField = AUTH_METHOD_SHAPE[watchedAuthMethod].user;

  const onSave = async (data: CreateFormData | UpdateFormData) => {
    try {
      const authUser = normalizeAuthUser(data.auth_method, data.auth_user);
      // Send the CA only when the admin typed one. Empty = keep existing
      // (edit) / use system roots (create); the field is never echoed back,
      // so an untouched edit must omit it rather than clear it.
      const caCert = data.tls_ca_cert?.trim() ? data.tls_ca_cert : undefined;
      if (isEditing && source) {
        const payload: Parameters<typeof updateMut.mutateAsync>[0] = {
          id: source.id,
          name: data.name,
          base_url: data.base_url,
          auth_method: data.auth_method,
          auth_user: authUser,
        };
        if (data.token) payload.token = data.token;
        if (caCert) payload.tls_ca_cert = caCert;
        await updateMut.mutateAsync(payload);
      } else {
        await createMut.mutateAsync({
          kind: data.kind,
          name: data.name,
          base_url: data.base_url,
          token: (data as CreateFormData).token,
          auth_method: data.auth_method,
          auth_user: authUser,
          tls_ca_cert: caCert,
        });
      }
      onClose();
    } catch {
      // surfaced via mutation state
    }
  };

  const busy = isSubmitting || createMut.isPending || updateMut.isPending;
  const mutationError = createMut.error || updateMut.error;

  return (
    <Dialog
      title={isEditing ? 'Edit tracker source' : 'Add tracker source'}
      onClose={onClose}
      dismissible={!busy}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
        <Controller
          control={control}
          name="kind"
          render={({ field }) => (
            <Select
              label="Kind"
              options={[
                { value: 'jira', label: 'Jira Cloud' },
                { value: 'ado', label: 'Azure DevOps' },
              ]}
              value={field.value as string}
              onChange={field.onChange}
              disabled={isEditing || busy}
              error={errors.kind?.message as string | undefined}
            />
          )}
        />
        <Input
          {...register('name')}
          label="Name"
          placeholder="Acme production Jira"
          error={errors.name?.message as string | undefined}
          disabled={busy}
          autoFocus={!isEditing}
        />
        <Input
          {...register('base_url')}
          label="Base URL"
          placeholder="https://acme.atlassian.net"
          error={errors.base_url?.message as string | undefined}
          disabled={busy}
        />
        <Controller
          control={control}
          name="auth_method"
          render={({ field }) => (
            <Select
              label="Auth method"
              options={[
                { value: 'api_token', label: 'Jira Cloud', description: 'email + API token' },
                { value: 'pat', label: 'Jira Server / Data Center', description: 'Personal Access Token' },
                { value: 'basic', label: 'Basic', description: 'username + password (legacy)' },
              ]}
              value={field.value as string}
              onChange={field.onChange}
              disabled={busy}
              error={errors.auth_method?.message as string | undefined}
            />
          )}
        />
        {userField && (
          <Input
            {...register('auth_user')}
            label={userField.label}
            placeholder={userField.placeholder}
            error={errors.auth_user?.message as string | undefined}
            disabled={busy}
          />
        )}
        <Input
          {...register('token')}
          type="password"
          label={secretFieldLabel(watchedAuthMethod, isEditing)}
          error={errors.token?.message as string | undefined}
          disabled={busy}
          hint={isEditing ? 'Rotation is immediate — the old token is discarded.' : undefined}
        />
        <div>
          <label htmlFor="tls_ca_cert" className="mb-1 block text-sm font-medium">
            Custom CA certificate (PEM) <span className="text-text-muted">— optional</span>
          </label>
          <textarea
            id="tls_ca_cert"
            {...register('tls_ca_cert')}
            rows={4}
            className="
              w-full rounded-sm border px-3 py-2 font-mono text-xs
              disabled:bg-bg-muted
            "
            placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'}
            disabled={busy}
          />
          <p className="mt-1 text-xs text-text-muted">
            {source?.has_custom_ca
              ? 'A custom CA is configured. Paste a new bundle to replace it, or leave empty to keep it.'
              : 'For on-prem trackers behind a private/corporate CA. Paste the root (plus any intermediates). Leave empty for Cloud.'}
          </p>
        </div>
        {mutationError instanceof Error && <VerifyErrorPanel message={mutationError.message} />}
        <DialogActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="size-4 animate-spin" /> {isEditing ? 'Saving…' : 'Adding…'}
              </>
            ) : isEditing ? (
              'Save'
            ) : (
              'Add source'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Classify a verify-credentials failure by the backend's message prefix
 * (set in `api/tracker.rs::verify_credentials`) and render the
 * appropriate icon + hint. Falls back to a generic error panel for
 * anything unexpected.
 */
function VerifyErrorPanel({ message }: { message: string }) {
  // Defensive cap — the backend truncates Jira error bodies server-side,
  // but be safe in case some future provider returns a long blob.
  const detail = message.length > 400 ? `${message.slice(0, 400)} … (truncated)` : message;

  if (message.startsWith('invalid credentials')) {
    return (
      <div className="flex items-start gap-2 rounded-sm border border-feedback-error bg-feedback-error/10 p-3 text-sm">
        <AlertCircle className="mt-0.5 size-4 shrink-0 text-feedback-error" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-feedback-error">Jira rejected this token.</p>
          <p className="mt-1 text-xs text-text-muted">Double-check that the token is active and has API access.</p>
          <p className="mt-1 font-mono text-xs break-all text-text-muted">{detail}</p>
        </div>
      </div>
    );
  }
  if (message.startsWith('tracker unreachable')) {
    return (
      <div className="flex items-start gap-2 rounded-sm border border-feedback-warning bg-feedback-warning/10 p-3 text-sm">
        <WifiOff className="mt-0.5 size-4 shrink-0 text-feedback-warning" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-feedback-warning">Couldn&apos;t reach the tracker.</p>
          <p className="mt-1 text-xs text-text-muted">
            The base URL may be wrong, or the service is temporarily unavailable.
          </p>
          <p className="mt-1 font-mono text-xs break-all text-text-muted">{detail}</p>
        </div>
      </div>
    );
  }
  return <p className="text-sm break-all text-feedback-error">{detail}</p>;
}

// Re-export for convenience — callers typically reach SourcesAdminPage only.
export type { TrackerKind };
